"use client"

import { Col, Row } from "antd"
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRightArrowLeft, faBolt, faCheck, faHeadset, faLock, faShield } from "@fortawesome/free-solid-svg-icons"
import { Fragment, type FC } from "react"
import { useTranslation } from "react-i18next"

// Không tự bọc container giới hạn độ rộng — nơi gọi (VisaPageClient, VisaSearchResultsClient)
// chịu trách nhiệm đặt trong đúng wrapper ".visa__hero-content" (mixin page-content-width) để
// luôn khớp độ rộng với box search cùng cấp, không tự tính lại 1 lần nữa gây lệch.
type VisaHeroHighlightsProps = {
  /** "default": 4 mục, nối nhau bằng mũi tên, xếp full-width (trang kết quả tìm kiếm).
   * "compact": chỉ 3 mục đầu, không mũi tên, rộng vừa nội dung — đứng cạnh tiêu đề trên
   * trang hub (/dich-vu-visa), đúng theo ảnh mockup. */
  variant?: "default" | "compact"
}

export const VisaHighlightIcon: FC<{ icon: IconDefinition }> = ({ icon }) => {
  if (icon === faShield) {
    return (
      <span className="visa__highlight-icon visa__highlight-icon--shield-check">
        <FontAwesomeIcon icon={faShield} />
        <FontAwesomeIcon icon={faCheck} className="visa__highlight-check" />
      </span>
    )
  }

  return (
    <span className="visa__highlight-icon">
      <FontAwesomeIcon icon={icon} />
    </span>
  )
}

const VisaHeroHighlights: FC<VisaHeroHighlightsProps> = ({ variant = "default" }) => {
  const { t } = useTranslation()

  // 4 mục nổi bật, mỗi mục bọc trong 1 khung bo tròn riêng (kiểu "radio option card") — nằm
  // ngay trong nền gradient của hero-band, dưới box search, xếp 1 hàng ngang (icon trái/text
  // phải), nối nhau bằng mũi tên đôi. Dùng chung cho cả trang hub (/dich-vu-visa) lẫn trang
  // kết quả tìm kiếm (/dich-vu-visa/ket-qua-tim-kiem) — cùng 1 bộ design box search.
  const highlights = [
    { icon: faShield, title: t("visa.highlights.rate_title"), desc: t("visa.highlights.rate_desc") },
    { icon: faBolt, title: t("visa.highlights.fast_title"), desc: t("visa.highlights.fast_desc") },
    { icon: faHeadset, title: t("visa.highlights.support_title"), desc: t("visa.highlights.support_desc") },
    { icon: faLock, title: t("visa.highlights.security_title"), desc: t("visa.highlights.security_desc") },
  ]

  if (variant === "compact") {
    return (
      <div className="visa__highlights visa__highlights--compact">
        {highlights.slice(0, 3).map((item) => (
          <div key={item.title} className="visa__highlight-item">
            <VisaHighlightIcon icon={item.icon} />
            <div className="visa__highlight-text">
              <strong>{item.title}</strong>
              <p>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <Row wrap={false} align="middle" className="visa__highlights">
      {highlights.map((item, index) => (
        <Fragment key={item.title}>
          <Col flex="1 1 0" className="visa__highlight-col">
            <div className="visa__highlight-item">
              <VisaHighlightIcon icon={item.icon} />
              <div className="visa__highlight-text">
                <strong>{item.title}</strong>
                <p>{item.desc}</p>
              </div>
            </div>
          </Col>
          {index < highlights.length - 1 && (
            <Col flex="0 0 auto" className="visa__highlight-arrow">
              <FontAwesomeIcon icon={faArrowRightArrowLeft} />
            </Col>
          )}
        </Fragment>
      ))}
    </Row>
  )
}

export default VisaHeroHighlights
